/**
 * Centralized Theme System for Find-a-Feeling
 * Single source of truth for all colors and styling
 *
 * To change colors app-wide, update the COLORS object below.
 */

import { cn } from "../lib/utils";

// ============================================================================
// COLOR DEFINITIONS - UPDATE THESE TO CHANGE COLORS THROUGHOUT THE APP
// ============================================================================

const COLORS = {
  // Dark Mode (default) - isLightMode = false
  dark: {
    // Backgrounds
    page: "bg-surface-default", // #1a0e2e - Deep indigo
    card: "bg-surface-alt", // #2d1f4a - Lighter indigo
    menu: "bg-brand-accent-mint", // #8ABFAA - Mint dropdown
    navActive: "bg-surface-muted", // #3a2d5c - Medium purple nav highlight

    // Text colors
    primary: "text-text-primary", // #F5F5F5 - Off-white main text
    title: "text-brand-secondary", // #D6A857 - Gold headings
    navText: "text-brand-accent-mint", // #8ABFAA - Mint nav items

    // Interactive colors
    navHover: "hover:text-brand-accent-coral", // #E08F87 - Coral hover
    buttonBg: "bg-brand-primary", // #4B3F72 - Deep purple
    buttonHover: "hover:bg-brand-primary/80",

    // Borders & accents
    border: "border-border-default", // #4a3a6b - Indigo border
    divider: "bg-brand-secondary", // #D6A857 - Gold divider
    focusRing: "focus:ring-brand-accent-mint", // #8ABFAA - Mint focus
    toggleBg: "bg-brand-secondary", // #D6A857 - Gold toggle
    toggleCircle: "bg-surface-default", // #1a0e2e - Dark circle
    navBorder: "border-surface-muted", // #3a2d5c - Nav border (not used in dark mode)
  },

  // Light Mode - isLightMode = true
  light: {
    // Backgrounds
    page: "bg-discovery-surface-default", // #addecb - Light mint
    card: "bg-discovery-surface-muted", // #b6dfb9 - Light green card
    menu: "bg-brand-accent-mint", // #8ABFAA - Mint dropdown
    navActive: "bg-surface-muted-light", // #594a7d - Light purple nav highlight

    // Text colors
    primary: "text-discovery-text-primary", // #2d4a2e - Dark green main text
    title: "text-brand-accent-coral", // #E08F87 - Coral headings
    navText: "text-brand-accent-coral", // #E08F87 - Coral nav items
    navInactive: "text-brand-primary", // #4B3F72 - Purple inactive nav

    // Interactive colors
    navHover: "hover:text-brand-accent-coral", // #E08F87 - Coral hover
    buttonBg: "bg-brand-accent-coral", // #E08F87 - Coral button
    buttonHover: "hover:bg-brand-accent-coral/80",

    // Borders & accents
    border: "border-discovery-border-default", // #7ab37d - Green border
    divider: "bg-surface-muted", // #3a2d5c - Purple divider
    focusRing: "focus:ring-brand-accent-mint", // #8ABFAA - Mint focus
    toggleBg: "bg-surface-muted", // #3a2d5c - Purple toggle
    toggleCircle: "bg-discovery-surface-default", // #addecb - Light mint circle
    navBorder: "border-surface-muted-light", // #594a7d - Purple nav border
  },
};

// ============================================================================
// THEME CONFIGURATION INTERFACE
// ============================================================================

interface ThemeConfig {
  colors: {
    bg: { primary: string; secondary: string };
    text: { primary: string; title: string; active: string };
    accent: { primary: string; hover: string };
  };
  components: {
    button: string;
    card: string;
    input: string;
  };
}

/**
 * Creates theme configuration object for legacy components
 */
export function createTheme(isLightMode: boolean = false): ThemeConfig {
  const mode = isLightMode ? COLORS.light : COLORS.dark;

  return {
    colors: {
      bg: {
        primary: mode.page,
        secondary: mode.card,
      },
      text: {
        primary: mode.primary,
        title: mode.title,
        active: mode.navText,
      },
      accent: {
        primary: mode.buttonBg,
        hover: mode.buttonHover,
      },
    },
    components: {
      button: cn(mode.buttonBg, mode.primary, mode.buttonHover),
      card: cn(mode.card, "border", mode.border),
      input: cn("border-2", mode.border, mode.card, mode.primary, "focus:ring-2", mode.focusRing),
    },
  };
}

// ============================================================================
// NAVIGATION STYLES - Clean, reusable navigation styling
// ============================================================================

/**
 * Get navigation link classes (text, colors, sizing)
 */
export function getNavLinkClass(
  isActive: boolean,
  isMobile: boolean = false,
  isLightMode: boolean = false
): string {
  const mode = isLightMode ? COLORS.light : COLORS.dark;

  return cn(
    // Base styles
    "transition-colors duration-200 rounded-md px-2 py-2",
    "inline-flex items-center justify-center overflow-hidden group",
    "focus:outline-none focus:ring-2 focus:ring-inset",
    mode.focusRing,

    // Size
    isMobile ? "w-full h-[40px] text-lg" : "w-[110px] h-[40px] text-base",

    // Font weight
    isActive ? "font-bold" : "font-medium",

    // Text color - different logic for light vs dark mode
    isActive
      ? mode.navText
      : isLightMode
        ? cn(COLORS.light.navInactive, mode.navHover)
        : cn(mode.navText, mode.navHover)
  );
}

/**
 * Get navigation item container classes (background, border, layout)
 */
export function getNavItemClass(
  isActive: boolean,
  isMobile: boolean = false,
  isLightMode: boolean = false
): string {
  const mode = isLightMode ? COLORS.light : COLORS.dark;

  return cn(
    "transition-colors duration-200 rounded-md",

    // Active state
    isActive && (isLightMode ? cn(mode.navActive, "border-2", mode.navBorder) : mode.navActive),

    // Layout
    isMobile ? "w-full text-center mb-2" : "flex items-center mr-2"
  );
}

// ============================================================================
// COMPONENT UTILITY FUNCTIONS - Reusable styling for common components
// ============================================================================

/**
 * Get page background class
 */
export function getPageBg(isLightMode: boolean = false): string {
  return isLightMode ? COLORS.light.page : COLORS.dark.page;
}

/**
 * Get card styling
 */
export function getCardClass(isLightMode: boolean = false): string {
  const mode = isLightMode ? COLORS.light : COLORS.dark;
  return cn(mode.card, "border rounded-lg", mode.border);
}

/**
 * Get button styling
 */
export function getButtonClass(isLightMode: boolean = false): string {
  const mode = isLightMode ? COLORS.light : COLORS.dark;
  return cn(
    mode.buttonBg,
    mode.primary,
    mode.buttonHover,
    "px-4 py-2 rounded-md font-medium transition-colors duration-200"
  );
}

/**
 * Get input styling
 */
export function getInputClass(isLightMode: boolean = false): string {
  const mode = isLightMode ? COLORS.light : COLORS.dark;
  return cn(
    "border-2 rounded-md px-3 py-2",
    mode.border,
    mode.card,
    mode.primary,
    "focus:outline-none focus:ring-2",
    mode.focusRing,
    "transition-colors duration-200"
  );
}

/**
 * Get text color class
 */
export function getTextClass(
  type: "primary" | "title" = "primary",
  isLightMode: boolean = false
): string {
  const mode = isLightMode ? COLORS.light : COLORS.dark;
  return type === "title" ? mode.title : mode.primary;
}

/**
 * Get divider styling
 */
export function getDividerClass(isLightMode: boolean = false): string {
  return isLightMode ? COLORS.light.divider : COLORS.dark.divider;
}

// ============================================================================
// NAVIGATION-SPECIFIC UTILITIES
// ============================================================================

/**
 * Get toggle button background
 */
export function getToggleBg(isLightMode: boolean = false): string {
  return isLightMode ? COLORS.light.toggleBg : COLORS.dark.toggleBg;
}

/**
 * Get toggle button circle styling
 */
export function getToggleCircle(isLightMode: boolean = false): string {
  return isLightMode ? COLORS.light.toggleCircle : COLORS.dark.toggleCircle;
}

/**
 * Get toggle button focus ring
 */
export function getToggleFocusRing(isLightMode: boolean = false): string {
  return isLightMode ? "focus:ring-surface-muted" : "focus:ring-brand-secondary";
}

/**
 * Get title text color
 */
export function getTitleColor(isLightMode: boolean = false): string {
  return isLightMode ? COLORS.light.title : COLORS.dark.title;
}

/**
 * Get hamburger menu button color
 */
export function getMenuButtonColor(isLightMode: boolean = false): string {
  const mode = isLightMode ? COLORS.light : COLORS.dark;
  return cn(
    mode.primary,
    isLightMode
      ? "hover:text-surface-muted-light focus:ring-surface-muted"
      : "hover:text-brand-accent-mint focus:ring-brand-accent-mint"
  );
}
